import { czechID } from "config";
import { action, computed, observable, toJS, set, runInAction } from "mobx";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import LocalizationService from "./localizationService";
import ChargeOrderStorageRepository from "../repositories/chargeOrderStorageRepository/chargeOrderStorageRepository";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeOrderItem, { ChargeOrderItemValidationRules } from "models/chargeOrderItem";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import { isAfter, isBefore } from "date-fns";
import OutageService from "./outageService";
import {
  FUEL_TYPE_NOT_ALLOWED,
  NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED,
  NOTIFICATION_SERVICE_INIT_FAILURE,
  NOTIFICATION_SERVICE_START_FAILURE,
  NOTIFICATION_SERVICE_STOP_FAILURE,
  OUTAGE_ACTIVATED,
  PERSISTENCE_ORDER_UPDATE_FAILURE,
  POST_CHARGE_ORDER_FAILURE,
  VALID_SINCE_INVALID,
} from "repositories/order/constants";
import EventBus, {
  multiOrderIsInvalid,
  orderIsInvalid,
  paymentFailed,
  paymentFuelTypeNotAllowed,
  paymentMultiFuelTypeNotAllowed,
  paymentValidSinceInvalid,
  preselectChargeType,
  proposedPurchageCharge,
} from "./eventBus";
import SignalRNotificationService from "./signalR/notification/signalRNotificationService";
import { IHasValidation, validate, validateAll } from "@frui.ts/validation";
import NotificationPreferences from "models/notificationPreferences";
import { PaymentType } from "models/payment";
import ChargeOrderResponse from "entities/chargeOrderResponse";
import copyChargeOrderDeep from "utils/copyChargeOrderDeep";
import PaymentResult from "models/paymentResult";
import GetApiV3ChargeProposedPurchaseQuery from "entities/getApiV3ChargeProposedPurchaseQuery";
import ChargeTypesService from "./chargeTypesService";
import * as Sentry from "@sentry/react";
import { daysValidFor } from "entities/chargeValidity";
import PriceService from "./priceService";
import { ChargeOrderItemBatchValidationRules } from "../models/chargeOrderItem";
import Vehicle from "models/vehicle";
import { forEach, isEqual } from "lodash";
import { dateToIsoDate, isSameOrBeforePragueDay, startOfCzechDay } from "utils/dateUtils";
import TimeService from "./timeService";
import EnumService from "./enumService";
import ChargeTypeResponse from "entities/chargeTypeResponse";

export const validForToNotificationDays = (tg: (code: string) => string, notifyDaysBefore?: number) => {
  const countRegex = /%count%/g;
  const daysRegex = /%days%/g;
  const localizedString = notifyDaysBefore === 2 ? tg("2days") : tg("days");
  const result = localizedString.replace(countRegex, notifyDaysBefore ? notifyDaysBefore.toString() : "");

  return tg("notificationEnabledInfo").replace(daysRegex, result);
};

export default class ChargeOrderService {
  private readonly countRegex = /%count%/g;
  private readonly indexRegex = /%index%/g;
  @observable paymentError: string;
  @observable chargeTypes: string;
  @observable earliestPossibleValidSinceDate: Date;
  @observable earliestPossibleValidSinceDaysBefore: number;
  @observable chargeOrder: ChargeOrder = new ChargeOrder();
  postChargeOrder: ChargeOrder = new ChargeOrder();
  chargePrice = this.priceService.chargePrice;
  orderItemTotal = this.priceService.chargeOrderItemTotal;

  constructor(
    private storage: ChargeOrderStorageRepository,
    private chargeTypeService: ChargeTypesService,
    private enumService: EnumService,
    private timeService: TimeService,
    private localizationService: LocalizationService,
    private notificationService: SignalRNotificationService,
    private outageService: OutageService,
    private priceService: PriceService,
    private chargeOrderRepository: ChargeOrderRepository,
    private eventBus: EventBus
  ) {
    this.getTransferPaymentRestrictions();
    const chargeOrder = this.storage.getLastChargeOrder();
    if (chargeOrder !== undefined) {
      this.chargeOrder = chargeOrder;
      this.attachOrderValidators(this.chargeOrder);
      // Don't reset order when user move in history
    } else {
      this.createNewChargeOrder();
    }
  }

  @computed
  get chargeOrderItem() {
    if (!this.chargeOrder.charges.length) {
      this.chargeOrder.charges.push(new ChargeOrderItem());
    }
    return this.chargeOrder.charges[0];
  }

  @computed
  get chargeOrderItems() {
    return this.chargeOrder.charges;
  }

  @computed
  get isTransferDisabled() {
    if (this.earliestPossibleValidSinceDate) {
      return this.chargeOrder.charges.reduce(
        (summary, item) =>
          summary ||
          (item.validity.validSinceDate !== undefined &&
            isAfter(
              startOfCzechDay(new Date(this.earliestPossibleValidSinceDate)),
              startOfCzechDay(new Date(item.validity.validSinceDate))
            )),
        false
      );
    }
    return false;
  }

  @computed
  get eshopOutage() {
    return this.outageService.eshopActiveOutage;
  }

  @computed
  get isOrderComplete() {
    return this.chargeOrder._status === ChargeOrderStatus.Paid || this.chargeOrder._status === ChargeOrderStatus.PaidBankTransfer;
  }

  @computed
  get payGateOutage() {
    return this.outageService.payGateActiveOutage;
  }

  @computed
  get areOrderItemsValid() {
    return this.chargeOrder.charges.reduce((isValid, item) => {
      const itemValidation = (item as any as IHasValidation<ChargeOrderItem>).__validation;
      return !!isValid && !!itemValidation?.isValid;
    }, true);
  }

  @computed
  get orderIsEmptyOrIncomplete() {
    if (!this.chargeOrder.charges.length) return true;

    for (const item of this.chargeOrder.charges) {
      if (!item.licensePlates.length || !item._chargeTypeId) return true;
    }

    return false;
  }

  @computed
  get isWholeOrderValid() {
    const isOrderValid = (this.chargeOrder as any as IHasValidation<ChargeOrder>).__validation?.isValid;
    const isNotificationPreferences = (this.chargeOrder.notificationPreferences as any as IHasValidation<NotificationPreferences>)
      .__validation?.isValid;
    return !!isOrderValid && !!isNotificationPreferences && this.areOrderItemsValid;
  }

  private async formatOrder() {
    const serverDate = await this.timeService.getCurrentServerTime();
    this.postChargeOrder.charges = this.postChargeOrder.charges.map((item: ChargeOrderItem) => {
      if (item.licensePlate && item.licensePlates.length === 0) {
        item.licensePlates = [item.licensePlate];
        item.licensePlate = undefined;
      }
      if (item.licensePlate && item.licensePlates.length > 0) {
        item.licensePlate = undefined;
      }
      if (item.validity.validSinceDate && isSameOrBeforePragueDay(item.validity.validSinceDate, serverDate)) {
        item.validity.hasImmediateValidity = true;
        item.validity.validSinceDate = undefined;
      }
      if (item.validity.validSinceDate && !isSameOrBeforePragueDay(item.validity.validSinceDate, serverDate)) {
        item.validity.hasImmediateValidity = undefined;
      }
      return item;
    });
    if (this.postChargeOrder.notificationPreferences.emailNotificationEnabled === false) {
      this.postChargeOrder.notificationPreferences.emailAddress = undefined;
    }
    if (
      this.postChargeOrder.notificationPreferences.emailNotificationEnabled &&
      this.postChargeOrder.notificationPreferences._emailAddressNotification
    ) {
      this.postChargeOrder.notificationPreferences.emailAddress =
        this.postChargeOrder.notificationPreferences._emailAddressNotification;
    }
    if (this.postChargeOrder.paymentType === PaymentType.GooglePay) {
      this.postChargeOrder.paymentType = PaymentType.PayGate;
    }
    this.postChargeOrder.id = "";
  }

  private async startOrderCreationProcess(turnstileToken: string | undefined | null) {
    return this.notificationService
      .serviceStart()
      .then(async () => {
        if (!this.notificationService.connectionId) {
          this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED }));
          return;
        }

        this.chargeOrderRepository.post(this.postChargeOrder, this.notificationService.connectionId, turnstileToken).catch(() => {
          this.eventBus.publish(paymentFailed({ message: POST_CHARGE_ORDER_FAILURE }));
        });
      })
      .catch(() => {
        this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_START_FAILURE }));
      });
  }

  private translateGeneral(code: string) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`);
  }

  private attachOrderValidators(chargeOrder?: ChargeOrder) {
    if (chargeOrder?._origin === "multiple") {
      attachAutomaticBlurValidator(chargeOrder || this.chargeOrder, ChargeOrderItemBatchValidationRules);
    } else {
      attachAutomaticBlurValidator(chargeOrder || this.chargeOrder, ChargeOrderItemValidationRules);
    }
  }

  @action.bound
  getPaymentConfirmation() {
    return this.chargeOrderRepository.getPaymentConfirmation(this.chargeOrder.id);
  }

  @action.bound
  setChargeOrderStatus(status: ChargeOrderStatus) {
    return this.storage.storeChargeStatus(status);
  }

  @action.bound
  getChargeOrderStatus() {
    return this.storage.getChargeStatus();
  }

  @action.bound
  clearChargeOrderStatus() {
    return this.storage.clearChargeStatus();
  }

  @action.bound
  notificationInfoMulti() {
    const orderItem = this.chargeOrderItem;
    let genericInfo = false;

    if (!orderItem) {
      return undefined;
    }

    forEach(this.chargeOrderItems, item => {
      if (item._chargeTypeId !== orderItem._chargeTypeId) {
        genericInfo = true;
      }
    });

    const chargeType = this.chargeTypeService.findChargeType(orderItem._chargeTypeId);
    if (genericInfo || !chargeType?.notifyDaysBefore) {
      return this.translateNotificationInfo("notificationEnabledInfoGenericMulti");
    }

    return validForToNotificationDays(this.translateNotificationInfo, chargeType.notifyDaysBefore);
  }

  @action.bound
  notificationInfoSingle() {
    const orderItem = this.chargeOrderItem;
    if (!orderItem) {
      return undefined;
    }

    const chargeType = this.chargeTypeService.findChargeType(orderItem._chargeTypeId);

    if (!chargeType?.notifyDaysBefore) {
      return this.translateNotificationInfo("notificationEnabledInfoGeneric");
    }

    return validForToNotificationDays(this.translateNotificationInfo, chargeType.notifyDaysBefore);
  }

  @action.bound
  translateNotificationInfo(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`eshop.order.contact.${code}`, undefined, disableUnbreakableRules);
  }

  @action.bound
  reopenOrder() {
    this.chargeOrder._status = ChargeOrderStatus.Open;
    this.chargeOrder._wasReopen = true;
    this.persistChargeOrder();
  }

  @action.bound
  preselectChargeType(days: number, chargeTypes: ChargeTypeResponse[]) {
    if (!this.chargeOrder.charges.length) return;
    const validFor = daysValidFor(days);
    this.chargeTypeService.setChargeTypes(chargeTypes);
    const chargeType = chargeTypes.find(item => item.validFor === validFor);
    this.chargeOrderItem._chargeTypeId = this.chargeOrderItem.chargeTypeId = chargeType?.id;
    if (chargeType?.id) {
      this.eventBus.publish(preselectChargeType({ chargeType }));
    }
    this.persistChargeOrder();
  }

  @action.bound
  async prefillPremadeOrder({ chargeId, proposedPurchaseRequestID }: GetApiV3ChargeProposedPurchaseQuery) {
    try {
      const proposedPurchase = (await this.chargeOrderRepository.getProposedPurchase({
        chargeId,
        proposedPurchaseRequestID,
      })) as any;
      if (proposedPurchase.errors) {
        return;
      }
      this.eventBus.publish(proposedPurchageCharge({ proposedPurchase }));

      const orderItem = this.chargeOrderItem;
      orderItem.licensePlate = orderItem._licensePlateConfirmation = proposedPurchase.vehicle?.licensePlate;
      orderItem.countryId = proposedPurchase.vehicle?.countryId;
      orderItem._fuelTypeId = proposedPurchase.fuelTypeId;
      orderItem._chargeTypeId = proposedPurchase.chargeTypeId;
      orderItem.validSince = orderItem.validity.validSinceDate = proposedPurchase.validity?.validSinceDate
        ? startOfCzechDay(proposedPurchase.validity?.validSinceDate)
        : undefined;
      orderItem.validity.hasImmediateValidity = proposedPurchase.validity?.hasImmediateValidity;
      orderItem._hasImmediateValidity = proposedPurchase.validity?.hasImmediateValidity;

      // When the user made a past order with a currently invalid charge type (e.g. cheaper 10day from 2020),
      // it will try to assign a currently valid charge type with the same validity (e.g. 10day from current year).
      const validChargeTypes = this.chargeTypeService.validChargeTypes;
      const prefilledValidChargeType = validChargeTypes.find(x => x.id == proposedPurchase.chargeTypeId);
      if (!prefilledValidChargeType) {
        const allChargeTypes = this.chargeTypeService.chargeTypes;
        const invalidPrefilledChargeType = allChargeTypes.find(x => x.id == proposedPurchase.chargeTypeId);

        if (invalidPrefilledChargeType) {
          const closestValidCharge = validChargeTypes.find(x => x.validFor == invalidPrefilledChargeType.validFor);
          orderItem._chargeTypeId = closestValidCharge?.id;
        } else {
          orderItem._chargeTypeId = undefined;
        }
      }
      const notificationPreferences = this.chargeOrder.notificationPreferences;
      this.chargeOrder.emailAddress = notificationPreferences._emailConfirmation = proposedPurchase.emailAddress ?? "";
      notificationPreferences.emailNotificationEnabled =
        proposedPurchase.notificationPreferences?.emailNotificationEnabled || false;

      notificationPreferences.smsNotificationEnabled = proposedPurchase.notificationPreferences?.smsNotificationEnabled || false;

      notificationPreferences._emailAddressNotification = proposedPurchase.notificationPreferences?.emailAddress || undefined;

      notificationPreferences._isNotificationEmailSame =
        proposedPurchase.notificationPreferences?.emailAddress === proposedPurchase.emailAddress &&
        !!proposedPurchase.notificationPreferences?.emailAddress &&
        !!proposedPurchase.emailAddress;
      notificationPreferences._notificationEnabled =
        !!proposedPurchase.notificationPreferences?.smsNotificationEnabled ||
        !!proposedPurchase.notificationPreferences?.emailNotificationEnabled;

      notificationPreferences.phoneNumber = proposedPurchase.notificationPreferences?.phoneNumber;

      this.chargeOrder.proposedPurchaseRequestId = proposedPurchase.proposedPurchaseRequestId;
      this.chargeOrder.notificationPreferences = notificationPreferences;
      this.persistChargeOrder();
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setTag("proposedPurchase", "prefill");
        Sentry.captureException(error);
      });
    }
  }

  @action.bound
  filterEmptyChargeOrderCharges() {
    if (this.chargeOrderItems.length > 1) {
      const noneEmptyOrderItems = this.chargeOrderItems.filter(item => item.licensePlates.length > 0);
      this.chargeOrder.charges = noneEmptyOrderItems.length ? noneEmptyOrderItems : [this.chargeOrder.charges[0]];
    }
  }

  @action.bound
  chargeLabel(chargeTypeId: string) {
    const chargeValidFor = this.chargeTypeService.validChargeTypes.find(charge => charge.id === chargeTypeId)?.validFor;
    if (!chargeValidFor) return "";
    return this.localizationService.translateAttribute("chargeType", chargeValidFor);
  }

  @action.bound
  async payOrder(
    turnstileToken: string | undefined | null,
    isSingleOrder: boolean,
    completion: (chargeOrder: ChargeOrderResponse) => void
  ) {
    if (this.eshopOutage !== undefined) {
      this.eventBus.publish(paymentFailed({ message: OUTAGE_ACTIVATED }));
      return;
    }
    this.postChargeOrder = toJS(this.chargeOrder);
    await this.formatOrder();
    await this.notificationService.initialize().catch(() => {
      this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_INIT_FAILURE }));
    });
    this.notificationService.subscribeToPostChargeOrderResult(async (chargeOrder?: ChargeOrderResponse, code?: string) => {
      if (code === VALID_SINCE_INVALID) {
        this.eventBus.publish(paymentValidSinceInvalid({ message: code }));
        return;
      }
      if (code === FUEL_TYPE_NOT_ALLOWED && isSingleOrder) {
        this.eventBus.publish(paymentFuelTypeNotAllowed({ message: code }));
        return;
      }
      if (code === FUEL_TYPE_NOT_ALLOWED && !isSingleOrder) {
        this.eventBus.publish(paymentMultiFuelTypeNotAllowed({ message: code }));
        return;
      }
      if (code) {
        this.eventBus.publish(paymentFailed({ message: code }));
        return;
      }

      if (!chargeOrder) {
        this.eventBus.publish(paymentFailed({ message: POST_CHARGE_ORDER_FAILURE }));
        return;
      }
      await Promise.all([
        await this.updateOrder(chargeOrder).catch(() => {
          this.eventBus.publish(paymentFailed({ message: PERSISTENCE_ORDER_UPDATE_FAILURE }));
        }),
        this.notificationService.serviceStop().catch(() => {
          this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_STOP_FAILURE }));
        }),
      ]);

      completion(chargeOrder);
    });

    await this.startOrderCreationProcess(turnstileToken);
  }

  @action.bound
  updatePaymentMethod() {
    if (this.isTransferDisabled && this.chargeOrder.paymentType === PaymentType.Transfer) {
      this.chargeOrder.paymentType = undefined;
    }
  }

  @action.bound
  async updateOrder(chargeOrder: ChargeOrderResponse) {
    const { charges, transferPaymentInstructions } = chargeOrder;
    const newOrder = copyChargeOrderDeep(this.chargeOrder);

    newOrder.charges.forEach((item, index) => {
      item.validity.validSinceDate = charges[index].validSince;
      item.validity._validUntilDate = charges[index].validUntil;
      item.countryId = charges[index].vehicle.countryId;
      item.licensePlate = charges[index].vehicle.licensePlate;
      if (charges.length === 1) {
        item.authCode = charges[index].authCode;
      }
    });

    newOrder._status = chargeOrder.status;
    newOrder._proposedAfterPurchaseRedirect = chargeOrder.proposedAfterPurchaseRedirect;
    newOrder.transferPaymentInstructions = transferPaymentInstructions;
    newOrder.id = chargeOrder.id;

    this.storage.addChargeOrder(newOrder);
    this.storage.removeChargeOrder(this.chargeOrder.id);
    this.chargeOrder = newOrder;
    this.persistStoredChargeOrder(this.chargeOrder);
  }

  @action.bound
  createNewChargeOrder(vehicle?: Vehicle) {
    this.chargeOrder = new ChargeOrder();
    if (vehicle) {
      this.chargeOrder.charges[0].countryId = vehicle.countryId;
      this.chargeOrder.charges[0].licensePlate = vehicle.licensePlate;
    } else if (this.localizationService.locale === "cs") {
      this.chargeOrder.charges[0].countryId = czechID;
    }
    this.storage.addChargeOrder(this.chargeOrder);
    const chargeOrder = this.storage.getLastChargeOrder();
    if (chargeOrder) {
      this.chargeOrder = chargeOrder;
    }
  }

  @action.bound
  validateOrder() {
    const hasValidItems = this.chargeOrder.charges.reduce((isValid, item) => {
      return validate(item) && isValid;
    }, true);
    const isValid = validateAll([this.chargeOrder, this.chargeOrder.notificationPreferences]) && hasValidItems;
    if (!isValid) {
      this.eventBus.publish(orderIsInvalid());
    }

    return isValid;
  }

  @action.bound
  validateMultiOrder() {
    const hasValidItems = this.chargeOrder.charges.reduce(item => {
      return validate(item);
    }, true);
    const isValid = validate(this.chargeOrder.notificationPreferences) && hasValidItems && validate(this.chargeOrder);
    if (!isValid) {
      this.eventBus.publish(multiOrderIsInvalid());
    }

    return isValid;
  }

  @action.bound
  orderPaidByBankTransfer() {
    const chargeOrderId = this.chargeOrder.id;
    this.chargeOrder._status = ChargeOrderStatus.PaidBankTransfer;
    this.persistChargeOrder();

    return chargeOrderId;
  }

  @action.bound
  async getStoredChargeOrder(chargeOrderId: string) {
    const order = await this.storage.getChargeOrder(chargeOrderId);
    this.attachOrderValidators(order);

    return order;
  }

  @action.bound
  persistChargeOrder() {
    if (!this.chargeOrder.id) return;

    const stored = this.storage.getChargeOrder(this.chargeOrder.id);
    const plainNew = toJS(this.chargeOrder);
    this.clearValidation(plainNew);

    if (!isEqual(stored, plainNew)) {
      this.storage.updateChargeOrder(this.chargeOrder);
    }
  }

  @action.bound
  persistStoredChargeOrder(chargeOrder: ChargeOrder) {
    if (!chargeOrder.id) return;

    const stored = this.storage.getChargeOrder(chargeOrder.id);
    const plain = toJS(chargeOrder);
    this.clearValidation(plain);

    if (!isEqual(stored, plain)) {
      this.storage.updateChargeOrder(plain);
    }
  }

  @action.bound
  translateVehicleCount(count: number, index: number) {
    const string = this.translateGeneral("vehicleCount");

    return string.replace(this.countRegex, count.toString()).replace(this.indexRegex, index.toString());
  }

  @action.bound
  addChargeOrderItem() {
    const item = new ChargeOrderItem();
    if (this.localizationService.locale === "cs") {
      item.countryId = czechID;
    }

    item.id = Math.random().toString();
    this.chargeOrder.charges.push(item);
    this.chargeOrder._origin = "multiple";
    return item;
  }

  @action.bound
  removeChargeOrderItem(chargeOrderItem: ChargeOrderItem) {
    this.chargeOrder.charges.splice(this.chargeOrder.charges.indexOf(chargeOrderItem), 1);
    if (this.chargeOrder.charges.length === 1) {
      this.chargeOrder.charges[0]._visible = true;
    }
  }

  @action.bound
  async updateChargeOrderStatus(paymentResult: PaymentResult, chargeOrder: ChargeOrder, chargeOrderId?: string) {
    switch (paymentResult) {
      case PaymentResult.SUCCESS:
        chargeOrder._status = ChargeOrderStatus.Paid;
        if (!chargeOrderId) {
          chargeOrderId = this.chargeOrder.id;
        }
        const remoteOrder = await this.chargeOrderRepository.get(chargeOrderId);
        this.updateOrder(remoteOrder);
        break;
      case PaymentResult.DEFERRED:
        chargeOrder._status = ChargeOrderStatus.Deferred;
        break;
      case PaymentResult.FAILURE:
        chargeOrder._status = ChargeOrderStatus.Failed;
        this.chargeOrder = chargeOrder;
        break;
    }

    this.persistStoredChargeOrder(chargeOrder);
  }

  @action.bound
  async getTransferPaymentRestrictions() {
    const res = await this.chargeOrderRepository.getTransferPaymentRestrictions();
    this.earliestPossibleValidSinceDaysBefore = res.transferPayment?.earliestPossibleValidSinceDaysBefore;
    this.earliestPossibleValidSinceDate = res.transferPayment?.earliestPossibleValidSinceDate;
    this.chargeOrder._earliestPossibleValidSinceDaysBefore = res.transferPayment?.earliestPossibleValidSinceDaysBefore;
    this.chargeOrder._earliestPossibleValidSinceDate = res.transferPayment?.earliestPossibleValidSinceDate;
    this.persistChargeOrder();
  }

  @action.bound
  resetNotificationPreferences(notificationPreferences: NotificationPreferences) {
    notificationPreferences._notificationEnabled = false;
    notificationPreferences.emailAddress = undefined;
    notificationPreferences._emailConfirmation = undefined;
    notificationPreferences._emailAddressNotification = undefined;
    notificationPreferences.emailNotificationEnabled = false;
    notificationPreferences._isNotificationEmailSame = false;
    notificationPreferences.smsNotificationEnabled = false;
    notificationPreferences.phoneNumber = undefined;

    return notificationPreferences;
  }

  @action.bound
  reset() {
    this.storage.clearChargeOrders();
    this.createNewChargeOrder();
  }

  @action.bound
  clearValidation(object: any) {
    for (const key in object) {
      if (key === "__validation") {
        delete object[key];
      } else if (typeof object[key] === "object" && object[key] !== null) {
        this.clearValidation(object[key]);
      }
    }
  }
}
